import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Ticker } from "@/components/TickerDrawer";
import ProfileLibraryModal from "@/components/ProfileLibraryModal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Radar, Play, Zap, Plus, Trash2, Save, RefreshCw, X, FileText,
  AlertTriangle, CheckCircle2, Info, TrendingUp, Copy, ChevronDown, ChevronUp,
  ArrowRight, Eye, Library,
} from "lucide-react";

// ---------- helpers ----------
const UNIVERSES = [
  { id: "SP100", label: "S&P 100" },
  { id: "SP100+WATCHLISTS", label: "S&P 100 + Watchlists" },
  { id: "ETF", label: "ETF Universe" },
  { id: "CUSTOM", label: "Custom Symbols" },
];
const MCAP_TIERS = [
  { id: "",       label: "Any" },
  { id: "SMALL",  label: "Small ($300M–$2B)" },
  { id: "MID",    label: "Mid ($2B–$10B)" },
  { id: "LARGE",  label: "Large ($10B–$200B)" },
  { id: "MEGA",   label: "Mega (>$200B)" },
];
const MISSING_POLICY = [
  { id: "exclude_missing", label: "Exclude symbols with missing data (safe default)" },
  { id: "include_missing", label: "Include them anyway (accept risk)" },
];
const MISSING_EARNINGS_POLICY = [
  { id: "exclude", label: "Exclude when earnings date unknown" },
  { id: "include", label: "Include even without earnings date" },
];
const SUGGESTED_PORTFOLIOS = ["", "LONG_TERM", "SWING", "TACTICAL"];

const EMPTY_CRITERIA = {
  universe: "SP100+WATCHLISTS",
  custom_symbols: [],
  min_price: 5,
  max_price: null,
  min_market_cap: 500_000_000,
  max_market_cap: null,
  market_cap_tier: "",
  min_avg_dollar_volume: 10_000_000,
  min_avg_daily_volume: null,
  min_relative_volume: null,
  min_abs_day_move_pct: null,
  max_abs_day_move_pct: null,
  exclude_earnings_within_days: 5,
  missing_earnings_policy: "exclude",
  missing_data_policy: "exclude_missing",
  security_type: "",
  exclude_leveraged: false,
  exclude_inverse: false,
  exclude_otc: false,
  exclude_owned: false,
  exclude_recently_proposed: false,
  include_sectors: [],
  exclude_sectors: [],
  suggested_portfolio: "",
  max_ai_candidates: 15,
};

function num(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ---------- criteria form ----------
function CriteriaEditor({ criteria, setCriteria }) {
  const set = (k, v) => setCriteria((c) => ({ ...c, [k]: v }));
  const isCustom = criteria.universe === "CUSTOM";
  return (
    <div className="space-y-4">
      <Section title="Universe">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {UNIVERSES.map((u) => (
            <button key={u.id}
              onClick={() => set("universe", u.id)}
              className={`chip cursor-pointer ${criteria.universe === u.id ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground"}`}
              data-testid={`universe-${u.id}`}>{u.label}</button>
          ))}
        </div>
        {isCustom && (
          <Textarea
            value={(criteria.custom_symbols || []).join(", ")}
            onChange={(e) => set("custom_symbols", e.target.value.split(/[\s,]+/).filter(Boolean).map((s) => s.toUpperCase()))}
            placeholder="AAPL, MSFT, NVDA, TSLA"
            className="!rounded-none mono text-xs mt-2 min-h-[64px]"
            data-testid="custom-symbols-input"/>
        )}
      </Section>

      <Section title="Price & Market Cap">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Min Price ($)"><NumInput v={criteria.min_price} onChange={(v) => set("min_price", num(v))} tid="min-price"/></Field>
          <Field label="Max Price ($)"><NumInput v={criteria.max_price} onChange={(v) => set("max_price", num(v))} tid="max-price"/></Field>
          <Field label="Min Market Cap ($)"><NumInput v={criteria.min_market_cap} onChange={(v) => set("min_market_cap", num(v))} tid="min-mcap"/></Field>
          <Field label="Max Market Cap ($)"><NumInput v={criteria.max_market_cap} onChange={(v) => set("max_market_cap", num(v))} tid="max-mcap"/></Field>
        </div>
        <div className="mt-2">
          <Field label="Market Cap Tier">
            <select value={criteria.market_cap_tier || ""}
              onChange={(e) => set("market_cap_tier", e.target.value)}
              className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60 text-foreground"
              data-testid="mcap-tier">
              {MCAP_TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Liquidity & Momentum">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Min ADV ($)"><NumInput v={criteria.min_avg_dollar_volume} onChange={(v) => set("min_avg_dollar_volume", num(v))} tid="min-adv"/></Field>
          <Field label="Min Avg Vol (shares)"><NumInput v={criteria.min_avg_daily_volume} onChange={(v) => set("min_avg_daily_volume", num(v))} tid="min-adv-shares"/></Field>
          <Field label="Min Rel Volume"><NumInput v={criteria.min_relative_volume} onChange={(v) => set("min_relative_volume", num(v))} tid="min-rvol"/></Field>
          <Field label="Max AI Candidates"><NumInput v={criteria.max_ai_candidates} onChange={(v) => set("max_ai_candidates", num(v))} tid="max-ai"/></Field>
          <Field label="Min |Day Move| %"><NumInput v={criteria.min_abs_day_move_pct} onChange={(v) => set("min_abs_day_move_pct", num(v))} tid="min-day-move"/></Field>
          <Field label="Max |Day Move| %"><NumInput v={criteria.max_abs_day_move_pct} onChange={(v) => set("max_abs_day_move_pct", num(v))} tid="max-day-move"/></Field>
        </div>
      </Section>

      <Section title="Earnings & Missing Data">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Exclude earnings within N days">
            <NumInput v={criteria.exclude_earnings_within_days} onChange={(v) => set("exclude_earnings_within_days", num(v))} tid="earn-days"/>
          </Field>
          <Field label="When earnings date is unknown">
            <select value={criteria.missing_earnings_policy || "exclude"}
              onChange={(e) => set("missing_earnings_policy", e.target.value)}
              className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60 text-foreground"
              data-testid="missing-earnings-policy">
              {MISSING_EARNINGS_POLICY.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="When any other data is missing">
            <select value={criteria.missing_data_policy || "exclude_missing"}
              onChange={(e) => set("missing_data_policy", e.target.value)}
              className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60 text-foreground"
              data-testid="missing-data-policy">
              {MISSING_POLICY.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-2 flex items-center gap-2 text-[11px] text-signal-yellow">
          <AlertTriangle size={12}/> Missing values are NEVER treated as zero.
        </div>
      </Section>

      <Section title="Security Type & Portfolio Fit">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Security Type">
            <select value={criteria.security_type || ""}
              onChange={(e) => set("security_type", e.target.value)}
              className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60 text-foreground"
              data-testid="security-type">
              <option value="">Any</option>
              <option value="stocks_only">Stocks only</option>
              <option value="etfs_only">ETFs only</option>
            </select>
          </Field>
          <Field label="Suggested Portfolio">
            <select value={criteria.suggested_portfolio || ""}
              onChange={(e) => set("suggested_portfolio", e.target.value)}
              className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60 text-foreground"
              data-testid="suggested-portfolio">
              {SUGGESTED_PORTFOLIOS.map((p) => <option key={p} value={p}>{p || "None"}</option>)}
            </select>
          </Field>
          <BoolField label="Exclude leveraged ETFs" v={criteria.exclude_leveraged} onChange={(v) => set("exclude_leveraged", v)} tid="excl-lev"/>
          <BoolField label="Exclude inverse ETFs" v={criteria.exclude_inverse} onChange={(v) => set("exclude_inverse", v)} tid="excl-inv"/>
          <BoolField label="Exclude OTC" v={criteria.exclude_otc} onChange={(v) => set("exclude_otc", v)} tid="excl-otc"/>
          <BoolField label="Exclude currently owned" v={criteria.exclude_owned} onChange={(v) => set("exclude_owned", v)} tid="excl-owned"/>
          <BoolField label="Exclude recently proposed (14d)" v={criteria.exclude_recently_proposed} onChange={(v) => set("exclude_recently_proposed", v)} tid="excl-proposed"/>
        </div>
      </Section>

      <Section title="Sectors (comma-separated)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Only include">
            <Input value={(criteria.include_sectors || []).join(", ")}
              onChange={(e) => set("include_sectors", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder="Technology, Healthcare"
              className="!rounded-none mono text-xs"
              data-testid="incl-sectors"/>
          </Field>
          <Field label="Exclude">
            <Input value={(criteria.exclude_sectors || []).join(", ")}
              onChange={(e) => set("exclude_sectors", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder="Energy, Real Estate"
              className="!rounded-none mono text-xs"
              data-testid="excl-sectors"/>
          </Field>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="grid-cell !p-3 space-y-2">
      <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return <div><div className="label-cap mb-1">{label}</div>{children}</div>;
}
function NumInput({ v, onChange, tid }) {
  return (
    <Input value={v === null || v === undefined ? "" : v}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
      inputMode="decimal"
      className="!rounded-none mono text-xs"
      data-testid={`input-${tid}`}/>
  );
}
function BoolField({ label, v, onChange, tid }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <Switch checked={!!v} onCheckedChange={onChange} data-testid={`toggle-${tid}`}/>
    </div>
  );
}

// ---------- funnel + candidates ----------
function FunnelBar({ preview }) {
  if (!preview) return null;
  const f = preview.funnel;
  const dr = preview.data_refresh || {};
  const cp = preview.cost_preview || {};
  const steps = [
    { k: "Universe",           v: f.starting_universe,      tone: "text-foreground" },
    { k: "Usable Data",        v: f.usable_market_data,     tone: "text-muted-foreground" },
    { k: "Passed Pre-Screen",  v: f.passed_deterministic,   tone: "text-[#0A84FF]" },
    { k: "Sent to AI",         v: f.sent_to_ai,             tone: "text-signal-green" },
  ];
  return (
    <div className="grid-cell !p-3" data-testid="scanner-funnel">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={12} className="text-[#0A84FF]"/>
        <span className="mono uppercase text-[10px] tracking-widest text-[#0A84FF]">Scan Funnel</span>
        <span className="ml-auto mono text-[10px] text-muted-foreground">
          data refreshed: {dr.refreshed?.length || 0} · reused: {dr.reused?.length || 0} · failed: {dr.failed?.length || 0}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-px bg-border/60 text-xs">
        {steps.map((s) => (
          <div key={s.k} className="p-2 bg-card/40">
            <div className="label-cap">{s.k}</div>
            <div className={`mono text-xl ${s.tone}`}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap text-[11px]">
        <span className="chip border-border/60 text-muted-foreground">Est AI cost: ${cp.estimated_ai_cost_usd?.toFixed(4)} / {cp.estimated_ai_candidates} candidates</span>
        <span className="text-[10px] text-muted-foreground italic">{cp.note}</span>
      </div>
    </div>
  );
}

function ExclusionsBox({ preview }) {
  const ex = preview?.exclusions_by_reason || {};
  const items = Object.entries(ex).sort((a, b) => b[1] - a[1]);
  if (items.length === 0) return null;
  return (
    <div className="grid-cell !p-3" data-testid="scanner-exclusions">
      <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground mb-2">Excluded by rule</div>
      <div className="flex flex-wrap gap-2">
        {items.map(([reason, count]) => (
          <span key={reason} className="chip border-border/60 text-muted-foreground" data-testid={`excl-${reason}`}>
            {reason.replace(/_/g, " ")} · {count}
          </span>
        ))}
      </div>
    </div>
  );
}

function CandidateRow({ c }) {
  const priceStr = c.price ? `$${c.price.toFixed(2)}` : "—";
  const dayStr = c.pct_change_day !== null && c.pct_change_day !== undefined ? `${c.pct_change_day >= 0 ? "+" : ""}${c.pct_change_day.toFixed(2)}%` : "N/A";
  const dayCls = c.pct_change_day == null ? "text-muted-foreground" : c.pct_change_day >= 0 ? "pnl-up" : "pnl-down";
  const mcap = c.market_cap ? `$${(c.market_cap / 1e9).toFixed(1)}B` : "N/A";
  const rvol = c.relative_volume == null ? "N/A" : c.relative_volume.toFixed(2) + "x";
  const de = c.days_to_earnings == null ? "N/A" : `${c.days_to_earnings}d`;
  return (
    <tr className="border-b border-border/40 hover:bg-secondary/20" data-testid={`candidate-${c.symbol}`}>
      <td className="p-2 mono font-medium"><Ticker symbol={c.symbol}/></td>
      <td className="p-2 text-xs text-muted-foreground truncate max-w-[160px]" data-testid={`candidate-${c.symbol}-company`}>
        {(() => {
          // Hide degraded "TICKER TICKER" — matches Opportunities.jsx
          // and services.opportunity_scanner.resolve_company_metadata.
          const name = c.company || "";
          return name && name.toUpperCase() !== (c.symbol || "").toUpperCase() ? name : "";
        })()}
      </td>
      <td className="p-2 text-xs text-muted-foreground">{c.sector || "—"}</td>
      <td className="p-2 mono text-xs">{priceStr}</td>
      <td className={`p-2 mono text-xs ${dayCls}`}>{dayStr}</td>
      <td className="p-2 mono text-xs">{mcap}</td>
      <td className="p-2 mono text-xs">{rvol}</td>
      <td className="p-2 mono text-xs">{de}</td>
      <td className="p-2 mono text-sm text-[#0A84FF]">{c.pre_screen_score}</td>
    </tr>
  );
}

function CandidatesTable({ preview }) {
  const candidates = preview?.ranked_candidates || [];
  if (!candidates.length) {
    return (
      <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground" data-testid="scanner-no-candidates">
        No candidates passed the deterministic pre-screen. Loosen a criterion above and try again.
      </div>
    );
  }
  return (
    <div className="grid-cell !p-0 overflow-auto" data-testid="scanner-candidates">
      <table className="w-full text-xs">
        <thead className="bg-card/60 border-b border-border/60 text-[10px] uppercase mono text-muted-foreground tracking-widest">
          <tr>
            <th className="p-2 text-left">Symbol</th>
            <th className="p-2 text-left">Company</th>
            <th className="p-2 text-left">Sector</th>
            <th className="p-2 text-left">Price</th>
            <th className="p-2 text-left">Day</th>
            <th className="p-2 text-left">MCap</th>
            <th className="p-2 text-left">RVol</th>
            <th className="p-2 text-left">Days to Earn</th>
            <th className="p-2 text-left">Score</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => <CandidateRow key={c.symbol} c={c}/>)}
        </tbody>
      </table>
    </div>
  );
}

// ---------- profile sidebar ----------
function ProfileList({ profiles, activeId, onSelect, onDelete, onNew }) {
  return (
    <div className="grid-cell !p-3 space-y-2" data-testid="profile-list">
      <div className="flex items-center gap-2">
        <FileText size={12} className="text-muted-foreground"/>
        <span className="mono uppercase text-[10px] tracking-widest text-muted-foreground">Scan Profiles</span>
        <button className="btn-ghost h-6 !px-1 text-[10px] ml-auto" onClick={onNew} data-testid="profile-new">
          <Plus size={11}/> New
        </button>
      </div>
      <div className="space-y-1">
        {profiles.map((p) => (
          <div key={p.id} className={`flex items-start gap-2 p-2 border-l-2 cursor-pointer text-xs ${activeId === p.id ? "border-l-[#0A84FF] bg-secondary/40" : "border-l-transparent hover:bg-secondary/25"}`}
               onClick={() => onSelect(p)} data-testid={`profile-item-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="mono font-medium truncate">{p.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{p.description || "—"}</div>
              {p.last_run_at && (
                <div className="text-[9px] mono uppercase text-muted-foreground mt-0.5">
                  last run: {new Date(p.last_run_at).toLocaleDateString()} · {p.last_result_count ?? 0} results
                </div>
              )}
            </div>
            <button className="text-[10px] text-muted-foreground hover:text-signal-red p-1"
                    onClick={(e) => { e.stopPropagation(); onDelete(p); }}
                    title="Delete" data-testid={`profile-delete-${p.id}`}>
              <Trash2 size={11}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- result card ----------
function ResultCard({ opp, onOpen }) {
  const verdictTone = /STRONG_BUY|BUY/.test(opp.verdict) ? "pnl-up" :
                        /STRONG_SELL|SELL/.test(opp.verdict) ? "pnl-down" :
                        "text-signal-yellow";
  const status = opp.status === "superseded" ? "Superseded" :
                    opp.status === "dismissed" ? "Dismissed" :
                    "Scanner Result";
  const change = opp.change_pct;
  const changeCls = change == null ? "text-muted-foreground" : change >= 0 ? "pnl-up" : "pnl-down";
  return (
    <div className="grid-cell !p-3 space-y-2" data-testid={`scan-result-${opp.symbol}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="mono font-medium text-base"><Ticker symbol={opp.symbol} size="lg"/></span>
        <span className="text-[11px] text-muted-foreground truncate max-w-[200px]"
              data-testid={`scan-result-${opp.symbol}-company`}>
          {(() => {
            // Defensive: hide redundant "TICKER TICKER" when the
            // company-name lookup returned the ticker itself. Matches
            // Opportunities.jsx OpportunityCard behavior.
            const name = opp.name || opp.company || "";
            return name && name.toUpperCase() !== (opp.symbol || "").toUpperCase()
              ? name : "";
          })()}
        </span>
        <span className={`chip !text-[10px] uppercase border-current ${verdictTone}`}>{opp.verdict?.replace(/_/g, " ")}</span>
        {opp.status !== "fresh" && (
          <span className="chip !text-[10px] uppercase border-signal-yellow/60 text-signal-yellow">{status}</span>
        )}
        <span className="ml-auto mono text-[10px] text-muted-foreground">rank #{opp.prescreen_rank ?? "—"}</span>
      </div>
      <div className="grid grid-cols-4 gap-px bg-border/40 text-[10px]">
        <div className="p-2 bg-card/40"><div className="label-cap">Price</div><div className="mono text-sm">${opp.price?.toFixed(2) ?? "—"}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Day</div><div className={`mono text-sm ${changeCls}`}>{change != null ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}%` : "N/A"}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Score</div><div className="mono text-sm text-[#0A84FF]">{opp.score ?? "—"}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Risk</div><div className="mono text-sm">{opp.risk_score ?? "—"}</div></div>
      </div>
      <div className="grid grid-cols-3 gap-px bg-border/40 text-[10px]">
        <div className="p-2 bg-card/40"><div className="label-cap">Entry</div><div className="mono">{opp.suggested_entry ? `$${opp.suggested_entry.toFixed(2)}` : "—"}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Stop</div><div className="mono">{opp.suggested_stop ? `$${opp.suggested_stop.toFixed(2)}` : "—"}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Target</div><div className="mono">{opp.suggested_target ? `$${opp.suggested_target.toFixed(2)}` : "—"}</div></div>
      </div>
      {opp.analyst_summary && (
        <div className="text-[11px] text-muted-foreground line-clamp-2">{opp.analyst_summary}</div>
      )}
      <div className="flex items-center gap-1 pt-1">
        <button className="btn-outline h-7 !px-2 text-[11px]" onClick={() => onOpen(opp)} data-testid={`scan-open-${opp.symbol}`}>
          <Eye size={11}/> Open Analysis
        </button>
        {opp.sector && <span className="chip !text-[10px] border-border/60 text-muted-foreground ml-auto">{opp.sector}</span>}
      </div>
    </div>
  );
}

// ---------- summary bar (collapsed criteria) ----------
function CriteriaSummaryBar({ session, profile, criteria, onEditCriteria, onRunAgain, onExport, onOpenInOpps }) {
  const funnel = session?.funnel || {};
  const created = session?.opportunities_count ?? 0;
  const started = session?.started_at ? new Date(session.started_at).toLocaleString() : "—";
  const cost = session?.actual_ai_cost_usd;
  const chips = [
    criteria?.universe && { label: `universe · ${criteria.universe}`, key: "u" },
    criteria?.min_price != null && { label: `≥$${criteria.min_price}`, key: "mp" },
    criteria?.max_price != null && { label: `≤$${criteria.max_price}`, key: "xp" },
    criteria?.min_market_cap && { label: `MCap ≥$${(criteria.min_market_cap/1e9).toFixed(1)}B`, key: "mc" },
    criteria?.min_avg_dollar_volume && { label: `ADV ≥$${(criteria.min_avg_dollar_volume/1e6).toFixed(0)}M`, key: "adv" },
    criteria?.exclude_earnings_within_days && { label: `no earn ≤${criteria.exclude_earnings_within_days}d`, key: "e" },
    criteria?.max_ai_candidates && { label: `top ${criteria.max_ai_candidates}`, key: "top" },
  ].filter(Boolean);
  return (
    <div className="grid-cell !p-3 space-y-2" data-testid="scan-summary-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText size={12} className="text-[#0A84FF]"/>
        <span className="mono text-sm">{profile?.name || "Custom scan"}</span>
        {profile?.version != null && <span className="mono text-[10px] text-muted-foreground">v{profile.version}</span>}
        <span className="mono text-[10px] text-muted-foreground">· {started}</span>
        <span className="chip !text-[10px] uppercase border-signal-green/60 text-signal-green">{created} results</span>
        {cost != null && <span className="mono text-[10px] text-muted-foreground">cost ${cost.toFixed(4)}</span>}
        <div className="ml-auto flex gap-1">
          <button className="btn-outline h-7 !px-2 text-[10px]" onClick={onEditCriteria} data-testid="scan-edit-criteria">
            <ChevronDown size={11}/> Edit Criteria
          </button>
          <button className="btn-outline h-7 !px-2 text-[10px]" onClick={onRunAgain} data-testid="scan-run-again">
            <RefreshCw size={11}/> Run Again
          </button>
          <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={onExport} data-testid="scan-export">
            <Copy size={11}/> Export
          </button>
          <button className="btn-ghost h-7 !px-2 text-[10px]" onClick={onOpenInOpps} data-testid="scan-open-opps">
            <ArrowRight size={11}/> Open in Opportunities
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {chips.map((c) => <span key={c.key} className="chip !text-[10px] border-border/60 text-muted-foreground">{c.label}</span>)}
      </div>
      {funnel.starting_universe != null && (
        <div className="grid grid-cols-4 gap-px bg-border/40 text-[10px]">
          <div className="p-2 bg-card/40"><div className="label-cap">Universe</div><div className="mono">{funnel.starting_universe}</div></div>
          <div className="p-2 bg-card/40"><div className="label-cap">Usable Data</div><div className="mono">{funnel.usable_market_data}</div></div>
          <div className="p-2 bg-card/40"><div className="label-cap">Pre-Screen</div><div className="mono text-[#0A84FF]">{funnel.passed_deterministic}</div></div>
          <div className="p-2 bg-card/40"><div className="label-cap">Sent to AI</div><div className="mono text-signal-green">{funnel.sent_to_ai}</div></div>
        </div>
      )}
    </div>
  );
}

// ---------- main page ----------
export default function Scanner() {
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [name, setName] = useState("Untitled Scan");
  const [description, setDescription] = useState("");
  const [criteria, setCriteria] = useState({ ...EMPTY_CRITERIA });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Results-In-Place state: sessionId + results are separate from live editing.
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(null);        // {id, funnel, criteria_snapshot, ...}
  const [results, setResults] = useState([]);           // opportunities produced by session
  const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);
  const confirm = useConfirm();
  const nav = useNavigate();

  const loadProfiles = async () => {
    const { data } = await api.get("/scanner/profiles");
    setProfiles(data.profiles || []);
    return data.profiles || [];
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/scanner/sessions/${sid}`);
      setSession(data.session);
      setResults(data.opportunities || []);
      setCriteriaCollapsed(true);
      // If we can find a matching profile, activate it — otherwise leave criteria editable.
      if (data.session?.profile_id) {
        const matched = (profiles || []).find((p) => p.id === data.session.profile_id);
        if (matched) selectProfile(matched);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load scan session");
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    (async () => {
      const ps = await loadProfiles();
      if (ps.length && !activeProfile) selectProfile(ps[0]);
      const sid = searchParams.get("scan_session_id");
      if (sid) loadSession(sid);
    })();
  }, []);/* eslint-disable-line */

  function selectProfile(p) {
    setActiveProfile(p);
    setName(p.name);
    setDescription(p.description || "");
    setCriteria({ ...EMPTY_CRITERIA, ...(p.criteria || {}) });
    setPreview(null);
    setDirty(false);
  }
  function newProfile() {
    setActiveProfile(null);
    setName("Untitled Scan");
    setDescription("");
    setCriteria({ ...EMPTY_CRITERIA });
    setPreview(null);
    setDirty(true);
  }
  async function deleteProfile(p) {
    const ok = await confirm.confirm({
      variant: "destructive", title: `Delete profile '${p.name}'?`,
      description: "This cannot be undone.", confirmLabel: "Delete", testId: `delete-profile-${p.id}`,
    });
    if (!ok) return;
    await api.delete(`/scanner/profiles/${p.id}`);
    toast.success(`Deleted ${p.name}`);
    const ps = await loadProfiles();
    if (activeProfile?.id === p.id) {
      ps.length ? selectProfile(ps[0]) : newProfile();
    }
  }
  async function saveProfile() {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), description, criteria };
      if (activeProfile) {
        const { data } = await api.put(`/scanner/profiles/${activeProfile.id}`, body);
        toast.success(`Updated ${data.name}`);
      } else {
        const { data } = await api.post(`/scanner/profiles`, body);
        toast.success(`Created ${data.name}`);
        setActiveProfile(data);
      }
      await loadProfiles();
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }
  async function doPreview() {
    setPreviewing(true);
    try {
      const { data } = await api.post("/scanner/preview", { criteria });
      setPreview(data);
      toast.success(`Pre-screen: ${data.funnel.passed_deterministic} passed of ${data.funnel.starting_universe}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  }
  async function doRun() {
    if (!preview) {
      toast.info("Run Preview first to see what will be sent to AI.");
      return;
    }
    if ((preview.funnel?.sent_to_ai || 0) === 0) {
      toast.error("Nothing to send to AI. Loosen criteria first.");
      return;
    }
    const ok = await confirm.confirm({
      variant: "confirm",
      title: `Run AI scan on ${preview.funnel.sent_to_ai} candidates?`,
      description: `This will consume approximately $${preview.cost_preview.estimated_ai_cost_usd?.toFixed(4)} of AI budget and create opportunity records.`,
      confirmLabel: "Run Scan",
      testId: "scanner-run-confirm",
    });
    if (!ok) return;
    setRunning(true);
    try {
      const body = activeProfile ? { profile_id: activeProfile.id } : { criteria };
      const { data } = await api.post("/scanner/run", body);
      const n = data?.opportunities?.length || 0;
      toast.success(`Scan complete — ${n} opportunities created`);
      await loadProfiles();
      // Load the created session into the results view (stays on page).
      if (data.scan_session_id) {
        setSearchParams({ scan_session_id: data.scan_session_id }, { replace: false });
        await loadSession(data.scan_session_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  }

  function openInOpps() {
    if (session?.id) nav(`/opportunities?scope=this_scan&scan_session_id=${session.id}`);
    else nav("/opportunities");
  }
  function exportSession() {
    if (!session) return;
    const rows = ["symbol,verdict,score,confidence,risk_score,price,change_pct,entry,stop,target,rank,status"];
    for (const o of results) {
      rows.push([o.symbol, o.verdict, o.score, o.confidence, o.risk_score, o.price,
                    o.change_pct, o.suggested_entry, o.suggested_stop, o.suggested_target,
                    o.prescreen_rank, o.status].join(","));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `scan_${session.id.slice(0, 8)}.csv`; a.click(); URL.revokeObjectURL(url);
  }
  function openOppDrawer(opp) {
    window.dispatchEvent(new CustomEvent("tm:open-drawer",
      { detail: { symbol: opp.symbol, tab: "chart" } }));
  }
  function newScan() {
    setSession(null); setResults([]); setCriteriaCollapsed(false);
    setSearchParams({}, { replace: true });
  }

  useEffect(() => { setDirty(true); }, [criteria, name, description]);

  const disableSave = saving || !dirty;
  const canRun = preview && preview.funnel?.sent_to_ai > 0;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="scanner-root">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-medium tracking-tight flex items-center gap-2"><Radar size={18}/> Advanced Scanner</h1>
          <p className="text-xs text-muted-foreground">Deterministic pre-screen first · AI only sees the candidates that actually pass · missing data never counts as zero.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button className="btn-outline" onClick={() => setLibraryOpen(true)} data-testid="scanner-library">
            <Library size={13}/> Profile Library
          </button>
          <button className="btn-outline" onClick={saveProfile} disabled={disableSave} data-testid="scanner-save">
            {saving ? <Loader2 className="animate-spin" size={13}/> : <Save size={13}/>}
            {activeProfile ? "Update Profile" : "Save Profile"}
          </button>
          <button className="btn-outline" onClick={doPreview} disabled={previewing} data-testid="scanner-preview">
            {previewing ? <Loader2 className="animate-spin" size={13}/> : <RefreshCw size={13}/>}
            Preview Matches
          </button>
          <button className="btn-primary" onClick={doRun} disabled={!canRun || running} data-testid="scanner-run">
            {running ? <Loader2 className="animate-spin" size={13}/> : <Play size={13}/>}
            Run Scan
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
        <ProfileList
          profiles={profiles}
          activeId={activeProfile?.id}
          onSelect={selectProfile}
          onDelete={deleteProfile}
          onNew={newProfile}
        />

        <div className="space-y-4 min-w-0">
          {session && criteriaCollapsed ? (
            <CriteriaSummaryBar
              session={session}
              profile={activeProfile}
              criteria={session.criteria_snapshot || criteria}
              onEditCriteria={() => setCriteriaCollapsed(false)}
              onRunAgain={() => { newScan(); setTimeout(() => doPreview(), 50); }}
              onExport={exportSession}
              onOpenInOpps={openInOpps}
            />
          ) : (
            <>
              <div className="grid-cell !p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Field label="Profile Name">
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Under $25 · Liquid Growth"
                           className="!rounded-none mono text-xs" data-testid="profile-name"/>
                  </Field>
                  <Field label="Description">
                    <Input value={description} onChange={(e) => setDescription(e.target.value)}
                           placeholder="What is this scan looking for?"
                           className="!rounded-none mono text-xs" data-testid="profile-description"/>
                  </Field>
                  <div className="flex items-end ml-auto">
                    <div className="text-[11px] text-muted-foreground">
                      <div className="flex items-center gap-1"><Info size={11}/> Preview is FREE (deterministic only). Run Scan is where AI cost is incurred.</div>
                    </div>
                  </div>
                  {session && (
                    <button className="btn-outline h-7 !px-2 text-[10px]" onClick={() => setCriteriaCollapsed(true)}
                            data-testid="scan-collapse-criteria">
                      <ChevronUp size={11}/> Back to Results
                    </button>
                  )}
                </div>
              </div>
              <CriteriaEditor criteria={criteria} setCriteria={setCriteria}/>
              {preview && !session && (
                <>
                  <FunnelBar preview={preview}/>
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] gap-3">
                    <CandidatesTable preview={preview}/>
                    <ExclusionsBox preview={preview}/>
                  </div>
                </>
              )}
            </>
          )}

          {session && (
            <div className="space-y-2" data-testid="scan-results">
              <div className="flex items-center gap-2">
                <TrendingUp size={12} className="text-signal-green"/>
                <span className="mono uppercase text-[10px] tracking-widest text-signal-green">Scan Results · {results.length}</span>
                <span className="ml-auto mono text-[10px] text-muted-foreground">Session {session.id.slice(0, 8)}</span>
              </div>
              {results.length === 0 ? (
                <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground" data-testid="scan-results-empty">
                  This scan generated no opportunities. Try loosening the criteria and running again.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {results.map((o) => (
                    <ResultCard key={o.id} opp={o} onOpen={openOppDrawer}/>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ProfileLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(p) => { selectProfile(p); newScan(); }}
      />
    </div>
  );
}
